import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SessionKit } from '@wharfkit/session';
import { ABI, Bytes, Serializer, Transaction } from '@wharfkit/antelope';
import { WebRenderer } from '@wharfkit/web-renderer';
import { WalletPluginAnchor } from '@wharfkit/wallet-plugin-anchor';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Message } from 'primereact/message';
import { ProgressBar } from 'primereact/progressbar';
import { Tag } from 'primereact/tag';
import configuration from './configuration.json';

// Agent spend governance: pending approvals, agent limits and the live payment feed of the
// agentgov contract. Everything is read straight from the chain; approve / reject / revoke are
// signed with Anchor by the logged-in account and broadcast directly (no backend involved).
// The contract itself enforces who may do what, so the buttons are only a convenience.
const CONTRACT = 'agentgovtst1';
const REFRESH_MS = 3000;
const EXPLORER = 'https://sepolia.basescan.org/tx/';
// Solid buttons with white text: a site-wide stylesheet paints red/green buttons with gradients,
// which made the outlined variants unreadable.
const BTN = { color: '#fff' };
const STATUS = {
    1: ['authorized', 'info'], 2: ['pending approval', 'warning'], 3: ['approved', 'info'],
    4: ['rejected', 'danger'], 5: ['settled', 'success'], 6: ['failed', 'danger'], 7: ['expired', null],
};

const endpoint = () => localStorage.getItem('bcEndpoint') || configuration.passer.BCEndPoints[0].url;
const usdc = (micro) => (Number(micro) / 1e6).toFixed(2);
const short = (s, n = 10) => (s && s.length > 2 * n ? `${s.slice(0, n)}…${s.slice(-6)}` : s);
const utc = (t) => new Date(`${t}Z`);
const fmtTime = (t) => utc(t).toLocaleTimeString();

// Accounts whose eosio.msig proposals are shown (the test proposer, the old admin, the chain owner).
const PROPOSERS = ['spikebagent1', 'agtadmin1111', 'boni'];
const abiCache = {};
const describeCache = {};

async function chainPost(path, body) {
    const r = await fetch(`${endpoint()}${path}`, { method: 'POST', body: JSON.stringify(body) });
    if (!r.ok) throw new Error(`chain API: HTTP ${r.status}`);
    return r.json();
}
async function abiOf(account) {
    if (!abiCache[account]) abiCache[account] = ABI.from((await chainPost('/v1/chain/get_abi', { account_name: account })).abi);
    return abiCache[account];
}
async function sha256hex(bytes) {
    const h = await crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(h)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
// Human-readable summary of a proposed transaction. For a contract upgrade it shows the SHA-256 of
// the new code, to compare with the build before approving.
async function describe(packedHex) {
    if (describeCache[packedHex]) return describeCache[packedHex];
    const trx = Serializer.decode({ data: Bytes.from(packedHex, 'hex'), type: Transaction });
    const lines = [];
    for (const a of trx.actions) {
        const account = String(a.account);
        const name = String(a.name);
        let text;
        try {
            const d = Serializer.decode({ data: a.data, type: name, abi: await abiOf(account) });
            if (account === 'eosio' && name === 'setcode') text = `new code for ${d.account}, sha256 ${await sha256hex(d.code.array)}`;
            else if (account === 'eosio' && name === 'setabi') text = `new ABI for ${d.account}`;
            else text = JSON.stringify(Serializer.objectify(d));
        } catch (e) {
            text = `(${a.data.length} bytes)`;
        }
        lines.push(`${account}::${name} ${text}`);
    }
    describeCache[packedHex] = { lines, expiration: String(trx.expiration) };
    return describeCache[packedHex];
}
async function loadProposals() {
    const out = [];
    for (const proposer of PROPOSERS) {
        const q = { code: 'eosio.msig', scope: proposer, json: true, limit: 50 };
        const [props, appr] = await Promise.all([
            chainPost('/v1/chain/get_table_rows', { ...q, table: 'proposal' }),
            chainPost('/v1/chain/get_table_rows', { ...q, table: 'approvals2' }),
        ]);
        for (const p of props.rows) {
            const a = appr.rows.find((x) => x.proposal_name === p.proposal_name) || { requested_approvals: [], provided_approvals: [] };
            out.push({
                key: `${proposer}/${p.proposal_name}`, proposer, name: p.proposal_name,
                requested: a.requested_approvals.map((x) => `${x.level.actor}@${x.level.permission}`),
                provided: a.provided_approvals.map((x) => `${x.level.actor}@${x.level.permission}`),
                ...(await describe(p.packed_transaction)),
            });
        }
    }
    return out;
}

async function rows(table, scope = CONTRACT, extra = {}) {
    const r = await fetch(`${endpoint()}/v1/chain/get_table_rows`, {
        method: 'POST',
        body: JSON.stringify({ code: CONTRACT, scope, table, json: true, limit: 200, ...extra }),
    });
    if (!r.ok) throw new Error(`chain API: HTTP ${r.status}`);
    return (await r.json()).rows;
}

const AgentGovernance = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(null);
    const [session, setSession] = useState(null);
    const [now, setNow] = useState(Date.now());

    const kitRef = useRef(null);
    if (!kitRef.current) {
        kitRef.current = new SessionKit({
            appName: 'passer-agentgov',
            chains: [{ id: configuration.passer.BCid, url: endpoint() }],
            ui: new WebRenderer(),
            walletPlugins: [new WalletPluginAnchor()],
        });
    }
    const actor = session ? String(session.permissionLevel.actor) : null;
    const level = session ? `${session.permissionLevel.actor}@${session.permissionLevel.permission}` : null;

    const refresh = useCallback(async () => {
        try {
            const [config, agents, policies, vendors, reqs] = await Promise.all([
                rows('config'), rows('agents'), rows('policies'), rows('vendors'),
                rows('payreqs', CONTRACT, { reverse: true, limit: 40 }),
            ]);
            const cfg = config[0];
            const day = Math.floor(Date.now() / 1000 / cfg.day_sec);
            const spend = await Promise.all(agents.map((a) =>
                rows('spend', a.agent, { lower_bound: String(day), upper_bound: String(day), limit: 1 })));
            const pendingAll = await rows('payreqs', CONTRACT, {
                index_position: '4', key_type: 'i64', lower_bound: '2', upper_bound: '2', limit: 100,
            });
            const proposals = await loadProposals().catch(() => []);
            setData({ cfg, agents, policies, vendors, reqs, pendingAll, proposals,
                      spend: Object.fromEntries(agents.map((a, i) => [a.agent, Number(spend[i][0]?.committed || 0)])) });
            setError(null);
        } catch (e) {
            setError(e.message);
        }
    }, []);

    useEffect(() => {
        kitRef.current.restore().then((s) => s && setSession(s)).catch(() => {});
        refresh();
        const id = setInterval(() => { if (!document.hidden) { refresh(); setNow(Date.now()); } }, REFRESH_MS);
        return () => clearInterval(id);
    }, [refresh]);

    const login = async () => {
        try {
            setSession((await kitRef.current.login()).session);
        } catch (e) {
            setError(e.message || String(e));
        }
    };
    const logout = async () => {
        if (session) await kitRef.current.logout(session).catch(() => {});
        setSession(null);
    };

    const act = async (label, name, payload, confirmText, account = CONTRACT) => {
        if (confirmText && !window.confirm(confirmText)) return;
        setBusy(label);
        setNotice(null);
        try {
            const r = await session.transact({
                actions: [{ account, name, authorization: [session.permissionLevel], data: payload }],
            });
            setNotice({ severity: 'success', text: `${label}: done (tx ${short(String(r.resolved?.transaction?.id || ''), 8)})` });
            setTimeout(refresh, 1200);
        } catch (e) {
            const m = String(e.message || e).match(/assertion failure with message: ([^\n]+)/);
            setNotice({ severity: 'error', text: `${label}: ${m ? m[1] : e.message || e}` });
        } finally {
            setBusy(null);
        }
    };

    if (!data) return <div style={{ padding: 20 }}>{error ? <Message severity="error" text={error} /> : 'Loading…'}</div>;

    const policyOf = Object.fromEntries(data.policies.map((p) => [p.agent, p]));
    const vendorOf = Object.fromEntries(data.vendors.map((v) => [v.id, v]));
    const isAdmin = actor === data.cfg.admin;
    const myPending = data.pendingAll.filter((r) => policyOf[r.agent]?.approver === actor);
    const vendorCell = (r) => {
        const v = vendorOf[r.vendor_id];
        return v ? <span title={v.pay_to}>{v.label || short(v.pay_to)}</span> : r.vendor_id;
    };
    const secondsLeft = (r) => Math.round((utc(r.expires) - now) / 1000);
    // PrimeReact redraws a cell only when its row data changes, so the state the cells depend
    // on (signed-in account, clock, action in flight, policy, spend) is put into the rows.
    const live = { _actor: actor, _now: now, _busy: busy, _admin: isAdmin };
    const pendingRows = (actor ? myPending : data.pendingAll).map((r) => ({ ...r, ...live }));
    const agentRows = data.agents.map((a) => ({ ...a, ...live, _policy: policyOf[a.agent], _spent: data.spend[a.agent] }));

    return (
        <div style={{ padding: 16, display: 'grid', gap: 16 }}>
            <Card title="Agent spend governance"
                  subTitle={`Contract ${CONTRACT} · budget day ${data.cfg.day_sec} s · approval window ${data.cfg.pending_ttl_sec} s`}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    {actor ? (
                        <>
                            <span>Signed in as <b>{level}</b></span>
                            {isAdmin && <Tag severity="danger" value="admin" />}
                            {data.policies.some((p) => p.approver === actor) && <Tag severity="warning" value="approver" />}
                            <Button label="Sign out" size="small" text onClick={logout} />
                        </>
                    ) : (
                        <Button label="Sign in with Anchor" icon="pi pi-key" onClick={login} />
                    )}
                </div>
                {notice && <Message style={{ marginTop: 12 }} severity={notice.severity} text={notice.text} />}
                {error && <Message style={{ marginTop: 12 }} severity="warn" text={`Refresh failed: ${error}`} />}
            </Card>

            <Card title={`Pending approvals (${actor ? myPending.length : data.pendingAll.length})`}>
                <DataTable value={pendingRows} size="small" dataKey="id"
                           emptyMessage={actor ? 'Nothing waiting for your approval.' : 'Nothing pending.'}>
                    <Column field="id" header="Request" />
                    <Column field="agent" header="Agent" />
                    <Column header="Amount" body={(r) => <b>{usdc(r.amount)} USDC</b>} />
                    <Column header="Vendor" body={vendorCell} />
                    <Column header="Requested" body={(r) => fmtTime(r.created)} />
                    <Column header="Time left" body={(r) => (secondsLeft(r) > 0 ? `${Math.floor(secondsLeft(r) / 60)}:${String(secondsLeft(r) % 60).padStart(2, '0')}` : 'expired')} />
                    <Column header="" body={(r) => (actor && policyOf[r.agent]?.approver === actor && secondsLeft(r) > 0 ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                            <Button label="Approve" icon="pi pi-check" size="small" severity="success" style={BTN} loading={busy === `Approve ${r.id}`}
                                    onClick={() => act(`Approve ${r.id}`, 'approve', { approver: actor, id: r.id },
                                        `Approve payment ${r.id}: ${usdc(r.amount)} USDC by ${r.agent}?`)} />
                            <Button label="Reject" icon="pi pi-times" size="small" severity="danger" style={BTN} loading={busy === `Reject ${r.id}`}
                                    onClick={() => {
                                        const reason = window.prompt(`Reason for rejecting payment ${r.id}?`, 'not needed');
                                        if (reason !== null) act(`Reject ${r.id}`, 'reject', { approver: actor, id: r.id, reason: reason.slice(0, 120) });
                                    }} />
                        </div>
                    ) : null)} />
                </DataTable>
            </Card>

            <Card title="Agents">
                <DataTable value={agentRows} size="small" dataKey="agent">
                    <Column field="agent" header="Agent" body={(a) => <span title={a.label}>{a.agent}</span>} />
                    <Column header="Status" body={(a) => (a.status === 1 ? <Tag severity="success" value="active" />
                        : <Tag severity="danger" value="revoked" title={a.note} />)} />
                    <Column header="Per payment" body={(a) => `${usdc(policyOf[a.agent]?.per_tx_max)}`} />
                    <Column header="Approval above" body={(a) => `${usdc(policyOf[a.agent]?.approval_threshold)}`} />
                    <Column header="Approver" body={(a) => policyOf[a.agent]?.approver} />
                    <Column header="Spent today / daily limit" style={{ minWidth: 220 }} body={(a) => {
                        const max = Number(policyOf[a.agent]?.daily_max || 0);
                        const used = data.spend[a.agent] || 0;
                        return (
                            <div>
                                <ProgressBar value={max ? Math.min(100, Math.round((100 * used) / max)) : 0} showValue={false} style={{ height: 8 }} />
                                <small>{usdc(used)} / {usdc(max)} USDC</small>
                            </div>
                        );
                    }} />
                    {isAdmin && <Column header="" body={(a) => (a.status === 1 ? (
                        <Button label="Revoke" icon="pi pi-ban" size="small" severity="danger" style={BTN} loading={busy === `Revoke ${a.agent}`}
                                onClick={() => {
                                    const reason = window.prompt(`Revoke ${a.agent}: reason?`, 'kill switch');
                                    if (reason !== null) act(`Revoke ${a.agent}`, 'revoke', { agent: a.agent, reason: reason.slice(0, 120) });
                                }} />
                    ) : (
                        <Button label="Reinstate" icon="pi pi-replay" size="small" severity="success" style={BTN} loading={busy === `Reinstate ${a.agent}`}
                                onClick={() => act(`Reinstate ${a.agent}`, 'reinstate', { agent: a.agent }, `Reinstate ${a.agent}?`)} />
                    ))} />}
                </DataTable>
            </Card>

            <Card title={`Multisig proposals (${data.proposals.length})`}
                  subTitle="Changes to multisig-controlled accounts. Sign in with the requested account and permission (e.g. boni@owner) to approve.">
                <DataTable value={data.proposals.map((p) => ({ ...p, _level: level, _busy: busy }))} size="small" dataKey="key"
                           emptyMessage="No open proposals.">
                    <Column header="Proposal" body={(p) => <span><b>{p.name}</b><br /><small>by {p.proposer}</small></span>} />
                    <Column header="Would execute" style={{ maxWidth: 420 }} body={(p) => (
                        <div style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                            {p.lines.map((l) => <div key={l}>{l}</div>)}
                        </div>)} />
                    <Column header="Approvals" body={(p) => (
                        <div>{p.requested.concat(p.provided.filter((x) => !p.requested.includes(x))).map((x) => (
                            <div key={x}>{p.provided.includes(x) ? '\u2705' : '\u23f3'} {x}</div>))}</div>)} />
                    <Column header="Expires" body={(p) => fmtTime(p.expiration)} />
                    <Column header="" body={(p) => (level ? (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {p.requested.includes(level) && (
                                <Button label="Approve" icon="pi pi-check" size="small" severity="success" style={BTN}
                                        loading={busy === `Approve ${p.key}`}
                                        onClick={() => act(`Approve ${p.key}`, 'approve',
                                            { proposer: p.proposer, proposal_name: p.name,
                                              level: { actor: session.permissionLevel.actor, permission: session.permissionLevel.permission } },
                                            `Approve proposal ${p.name} as ${level}?\n\n${p.lines.join('\n')}`, 'eosio.msig')} />)}
                            {p.provided.length > 0 && (
                                <Button label="Execute" icon="pi pi-play" size="small" severity="info" style={BTN}
                                        loading={busy === `Execute ${p.key}`}
                                        onClick={() => act(`Execute ${p.key}`, 'exec',
                                            { proposer: p.proposer, proposal_name: p.name, executer: actor },
                                            `Execute proposal ${p.name}? It only succeeds with enough approvals.`, 'eosio.msig')} />)}
                        </div>) : null)} />
                </DataTable>
            </Card>

            <Card title="Payment feed (latest 40)">
                <DataTable value={data.reqs} size="small" paginator rows={15} dataKey="id">
                    <Column field="id" header="Request" />
                    <Column header="Time" body={(r) => fmtTime(r.updated)} />
                    <Column field="agent" header="Agent" />
                    <Column header="Amount" body={(r) => `${usdc(r.amount)} USDC`} />
                    <Column header="Vendor" body={vendorCell} />
                    <Column header="Status" body={(r) => <Tag severity={STATUS[r.status]?.[1]} value={STATUS[r.status]?.[0] || r.status} />} />
                    <Column header="Approved by" field="approved_by" />
                    <Column header="Public payment" body={(r) => (r.ext_txid
                        ? <a href={EXPLORER + r.ext_txid} target="_blank" rel="noreferrer">{short(r.ext_txid, 8)}</a> : '')} />
                </DataTable>
            </Card>
        </div>
    );
};

export default AgentGovernance;
